import * as React from 'react';
import {
  Box,
  focusVisibleOverrideOuter,
  SkeletonRectangle,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import {
  Pagination,
  type PaginationPage,
} from '@leagueplatform/genesis-core-alpha';

const SKELETON_HEIGHT_FALLBACK = '480px';

type BasePaginatedChatSessionListProps = {
  children?: React.ReactNode;
  /**
   * Text announced to screen readers via `aria-live="polite"` after page
   * transitions. In cursor mode this is especially important since there
   * are no page numbers for users to orient by — callers should provide a
   * value that changes on each navigation (e.g. "Showing newer results").
   */
  pageAnnouncement?: string;
  isLoading?: boolean;
  onItemClick?: (index: number) => void;
  name?: string;
};

type PagePaginationProps = BasePaginatedChatSessionListProps & {
  paginationStrategy?: 'page';
  currentPage: number;
  totalPages: number;
  onNavigate?: (page: number) => void;
};

type CursorPaginationProps = BasePaginatedChatSessionListProps & {
  paginationStrategy: 'cursor';
  nextPage?: string;
  previousPage?: string;
  /** Cursor for the first page. Provided by the backend but not yet surfaced in the UI. */
  firstPage?: string;
  /** Cursor for the last page. Provided by the backend but not yet surfaced in the UI. */
  lastPage?: string;
  onNavigate?: (cursor: string) => void;
};

export type PaginatedChatSessionListProps =
  | PagePaginationProps
  | CursorPaginationProps;

export const PaginatedChatSessionList = React.forwardRef<
  HTMLUListElement,
  PaginatedChatSessionListProps
>((props, ref) => {
  const {
    children,
    pageAnnouncement,
    isLoading = false,
    onItemClick,
    name,
  } = props;

  const isCursorMode = props.paginationStrategy === 'cursor';

  // Narrow strategy-specific props into stable locals for hook deps
  const currentPage = isCursorMode ? undefined : props.currentPage;
  const totalPages = isCursorMode ? undefined : props.totalPages;
  const onNavigateOffset = isCursorMode ? undefined : props.onNavigate;
  const nextCursor = isCursorMode ? props.nextPage : undefined;
  const previousCursor = isCursorMode ? props.previousPage : undefined;
  const onNavigateCursor = isCursorMode ? props.onNavigate : undefined;

  // Fallback screen-reader announcement for cursor pagination when the
  // caller doesn't provide pageAnnouncement. We detect loading→loaded
  // transitions and use a clear→set cycle so aria-live re-fires every time.
  const [cursorAnnouncement, setCursorAnnouncement] = React.useState('');
  const wasLoadingRef = React.useRef(false);
  React.useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = isLoading;

    if (wasLoading && !isLoading && isCursorMode && !pageAnnouncement) {
      setCursorAnnouncement('');
      const frameId = requestAnimationFrame(() => {
        setCursorAnnouncement('Results updated');
      });
      return () => cancelAnimationFrame(frameId);
    }
    return undefined;
  }, [isLoading, isCursorMode, pageAnnouncement]);

  const items = React.Children.toArray(children);
  const containerRef = React.useRef<HTMLUListElement | null>(null);
  const announcerRef = React.useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = React.useState(0);

  // Capture the list height while content is visible so the skeleton
  // can match it, preventing layout shift during page transitions.
  const lastContentHeightRef = React.useRef<number | undefined>();
  React.useLayoutEffect(() => {
    if (!isLoading && containerRef.current) {
      lastContentHeightRef.current = containerRef.current.offsetHeight;
    }
  }, [isLoading, children]);

  const skeletonHeight = lastContentHeightRef.current
    ? `${lastContentHeightRef.current}px`
    : SKELETON_HEIGHT_FALLBACK;

  const getButtons = React.useCallback((): HTMLButtonElement[] => {
    if (!containerRef.current) return [];
    return Array.from(
      containerRef.current.querySelectorAll<HTMLButtonElement>('li > button'),
    );
  }, []);

  // When focus leaves the list, reset focusedIndex to the selected item
  // so that tabbing back in lands on the selected chat, not the last
  // arrow-key position.
  const handleBlur = React.useCallback(
    (event: React.FocusEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.relatedTarget as Node)
      ) {
        const buttons = getButtons();
        const selectedIndex = buttons.findIndex(
          (btn) => btn.getAttribute('aria-current') === 'true',
        );
        if (selectedIndex !== -1) {
          setFocusedIndex(selectedIndex);
        }
      }
    },
    [getButtons],
  );

  // Clamp focusedIndex when the item count shrinks (e.g. pagination)
  React.useEffect(() => {
    const buttons = getButtons();
    if (buttons.length > 0 && focusedIndex >= buttons.length) {
      setFocusedIndex(0);
    }
  }, [focusedIndex, getButtons, children]);

  // Apply roving tabindex whenever focusedIndex or items change
  React.useEffect(() => {
    const buttons = getButtons();
    buttons.forEach((btn, i) => {
      // eslint-disable-next-line no-param-reassign
      btn.tabIndex = i === focusedIndex ? 0 : -1;
    });
  }, [focusedIndex, getButtons, children]);

  const handleItemClick = React.useCallback(
    (event: React.MouseEvent) => {
      const target = (event.target as HTMLElement).closest('li');
      if (!target || !containerRef.current) return;

      const listItems = Array.from(containerRef.current.querySelectorAll('li'));
      const clickedIndex = listItems.indexOf(target);
      if (clickedIndex === -1) return;

      setFocusedIndex(clickedIndex);
      onItemClick?.(clickedIndex);
    },
    [onItemClick],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      const buttons = getButtons();
      const count = buttons.length;
      if (count === 0) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        const next = (focusedIndex + 1) % count;
        setFocusedIndex(next);
        buttons[next]?.focus();
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const prev = (focusedIndex - 1 + count) % count;
        setFocusedIndex(prev);
        buttons[prev]?.focus();
      }
    },
    [focusedIndex, getButtons],
  );

  const handleOffsetPageChange = React.useCallback(
    (page: PaginationPage) => {
      onNavigateOffset?.(page.pageNumber);
    },
    [onNavigateOffset],
  );

  const handleCursorPageChange = React.useCallback(
    (page: PaginationPage) => {
      let cursor: string | undefined;
      if (page.buttonType === 'next') {
        cursor = nextCursor;
      } else if (page.buttonType === 'previous') {
        cursor = previousCursor;
      }
      if (cursor) {
        onNavigateCursor?.(cursor);
      }
    },
    [nextCursor, previousCursor, onNavigateCursor],
  );

  const showPagination = isCursorMode
    ? !!(nextCursor || previousCursor)
    : (totalPages ?? 0) > 1;

  if (items.length === 0 && !isLoading) return null;

  return (
    <Box>
      <Box
        as="ul"
        role="list"
        ref={(node: HTMLUListElement | null) => {
          containerRef.current = node;
          if (typeof ref === 'function') ref(node);
          // eslint-disable-next-line no-param-reassign
          else if (ref) ref.current = node;
        }}
        tabIndex={-1}
        data-testid="chat-session-list"
        aria-label={name}
        aria-busy={isLoading || undefined}
        onClick={handleItemClick}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        css={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          border: '1px solid $onSurfaceBorderSubdued',
          borderRadius: '$medium',
          overflow: 'hidden',
          ...focusVisibleOverrideOuter,
          '@mobile': {
            border: 'none',
            borderRadius: 0,
          },
          '@mobileLandscape': {
            border: 'none',
            borderRadius: 0,
          },
          '@tablet': {
            border: 'none',
            borderRadius: 0,
          },
        }}
      >
        {isLoading ? (
          <Box as="li">
            <SkeletonRectangle
              height={skeletonHeight}
              width="100%"
              aspectRatio="auto"
            />
          </Box>
        ) : (
          items.map((item, i) => (
            <Box
              as="li"
              role="listitem"
              key={(item as React.ReactElement).key ?? i}
              css={{
                borderBlockEnd:
                  i < items.length - 1
                    ? '$borderWidths$thin solid $onSurfaceBorderSubdued'
                    : undefined,
                '@mobile': {
                  borderBlockEnd:
                    '$borderWidths$thin solid $onSurfaceBorderSubdued',
                },
                '@mobileLandscape': {
                  borderBlockEnd:
                    '$borderWidths$thin solid $onSurfaceBorderSubdued',
                },
                '@tablet': {
                  borderBlockEnd:
                    '$borderWidths$thin solid $onSurfaceBorderSubdued',
                },
              }}
            >
              {item}
            </Box>
          ))
        )}
      </Box>
      {showPagination && (
        <Box
          css={{
            paddingBlockStart: '$oneAndHalf',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {isCursorMode ? (
            <Pagination
              compactMode
              onPageChange={handleCursorPageChange}
              isNextPageActiveOverride={!!nextCursor}
              isPrevPageActiveOverride={!!previousCursor}
            />
          ) : (
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={handleOffsetPageChange}
            />
          )}
        </Box>
      )}
      <VisuallyHidden ref={announcerRef} aria-live="polite">
        {pageAnnouncement || cursorAnnouncement}
      </VisuallyHidden>
    </Box>
  );
});

PaginatedChatSessionList.displayName = 'PaginatedChatSessionList';
